/**
 * Theme: "system" | "light" | "dark", stored per user (PATCH /api/me) and
 * mirrored in localStorage so the first paint is right before /api/me loads.
 * The resolved theme is written to <html data-theme>.
 */
import { useSyncExternalStore } from "react";
import type { Theme } from "./api";

const STORAGE_KEY = "carno.theme";
const listeners = new Set<() => void>();
let current: Theme = "system";
let media: MediaQueryList | null = null;

function systemPrefersDark(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function resolveTheme(theme: Theme): "light" | "dark" {
  if (theme === "system") return systemPrefersDark() ? "dark" : "light";
  return theme;
}

function apply(): void {
  document.documentElement.dataset.theme = resolveTheme(current);
  for (const l of listeners) l();
}

function isTheme(v: unknown): v is Theme {
  return v === "system" || v === "light" || v === "dark";
}

/** Call once before the first render. */
export function initTheme(): void {
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    stored = null;
  }
  current = isTheme(stored) ? stored : "system";
  if (typeof window.matchMedia === "function") {
    media = window.matchMedia("(prefers-color-scheme: dark)");
    media.addEventListener("change", () => {
      if (current === "system") apply();
    });
  }
  apply();
}

export function setTheme(theme: Theme): void {
  current = theme;
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Private mode: the server copy still applies on the next load.
  }
  apply();
}

export function getTheme(): Theme {
  return current;
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** The resolved theme ("light" | "dark"), for components like the toaster. */
export function useResolvedTheme(): "light" | "dark" {
  return useSyncExternalStore(
    subscribe,
    () => resolveTheme(current),
    () => "light" as const,
  );
}

export function useThemePreference(): Theme {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => "system" as Theme,
  );
}
